import { z } from 'zod'
import {
  contractBooksEffectSchema,
  contractPolicyFlagCodeSchema,
  contractPolicyFlagSchema,
  contractTurnaroundReadingSchema,
} from './contract-read.js'

/**
 * The Cadence contract. Every shape here is either lifted directly from the
 * real receipt fixtures (`backend/src/cadence/fixtures/*.json`, copied from
 * the marketing site's own receipt data) or, where the fixtures don't yet
 * carry what the console needs, the forward contract Cadence needs to grow
 * — see `docs/cadence-contract.md`. The fixture-backed fake server in
 * `backend/src/cadence/fake-server.ts` is what Phase 4 builds against until
 * real Cadence exists; this file is what both sides agree to.
 */

// ---------------------------------------------------------------------------
// The declared step list — addendum §G.
//
// Not present in the fixtures as a single field today. `missing` has the
// right per-step shape (order/intent/typeKey/activityId) but only lists
// steps that never completed, and disappears (`[]`) the moment a run
// succeeds — exactly when Run Detail most needs to show the step list. This
// is the fix: every declared step, always, each carrying where its evidence
// lives.
// ---------------------------------------------------------------------------

export const stepStatusSchema = z.enum(['evidenced', 'missing'])
export type StepStatus = z.infer<typeof stepStatusSchema>

export const stepEvidenceSchema = z
  .object({
    kind: z.enum(['row', 'check']),
    seq: z.number(),
  })
  .nullable()
export type StepEvidence = z.infer<typeof stepEvidenceSchema>

export const declaredStepSchema = z.object({
  order: z.number(),
  intent: z.string(),
  typeKey: z.string(),
  activityId: z.string(),
  status: stepStatusSchema,
  evidence: stepEvidenceSchema,
})
export type DeclaredStep = z.infer<typeof declaredStepSchema>

// ---------------------------------------------------------------------------
// The raw event journal — occurred/recorded, the two-clock rule.
// ---------------------------------------------------------------------------

export const eventRowSchema = z.object({
  seq: z.number(),
  eventType: z.string(),
  occurredAt: z.string(),
  recordedAt: z.string(),
  driftMs: z.number(),
  driftNote: z.string().nullable(),
})
export type EventRow = z.infer<typeof eventRowSchema>

// ---------------------------------------------------------------------------
// Verification checks. `outcome`/`verdict` are left as `z.string()` rather
// than a strict enum — the two observed so far are "Confirmed" and "Doesn't
// match — needs you" for outcome, "attest" and "refute" for verdict, but a
// receipt-rendering contract has no business rejecting a run over a new
// value Cadence adds; render what arrives.
// ---------------------------------------------------------------------------

export const checkSchema = z.object({
  seq: z.number(),
  outcome: z.string(),
  statement: z.string(),
  reason: z.string().nullable(),
  observedAt: z.string(),
  verdict: z.string(),
})
export type Check = z.infer<typeof checkSchema>

// ---------------------------------------------------------------------------
// Exceptions. `statement` is optional on a needs-a-decision entry —
// observed present in `mismatch.json`, absent in `refused.json`.
// `couldNotBeDetermined`'s shape is unobserved (empty in every fixture so
// far); modelled the same as `needsADecision` until a real one is seen.
// ---------------------------------------------------------------------------

export const exceptionEntrySchema = z.object({
  lane: z.string(),
  seq: z.number(),
  predicate: z.string(),
  reason: z.string(),
  retriggerBy: z.string().nullable(),
  statement: z.string().optional(),
})
export type ExceptionEntry = z.infer<typeof exceptionEntrySchema>

export const exceptionsSchema = z.object({
  needsADecision: z.array(exceptionEntrySchema),
  couldNotBeDetermined: z.array(exceptionEntrySchema),
})
export type Exceptions = z.infer<typeof exceptionsSchema>

// ---------------------------------------------------------------------------
// The triptych — expected / claimed / observed, each carrying its own
// epistemic status. `null` on a run that never reached the point of having
// one (both `refused.json` and `mismatch.json` — parked before or during
// verification).
// ---------------------------------------------------------------------------

export const triptychFactSchema = z.object({
  label: z.string(),
  value: z.string(),
})

export const triptychPanelSchema = z.object({
  epistemic: z.string(),
  summary: z.string(),
  facts: z.array(triptychFactSchema),
  valuesAt: z.string().nullable(),
})

export const triptychSchema = z
  .object({
    expected: triptychPanelSchema,
    claimed: triptychPanelSchema,
    observed: triptychPanelSchema,
    comparedOver: z.string(),
  })
  .nullable()
export type Triptych = z.infer<typeof triptychSchema>

// ---------------------------------------------------------------------------
// Content-addressed blobs referenced by the triptych's `valuesAt` pointers.
// ---------------------------------------------------------------------------

export const resolvedBlobSchema = z.object({
  kind: z.literal('present'),
  content: z.string(),
})

export const resolvedSchema = z.record(z.string(), resolvedBlobSchema)

export const addressedToSchema = z
  .object({
    customer: z.string(),
    says: z.string(),
  })
  .nullable()

// ---------------------------------------------------------------------------
// The full receipt — Run Detail's data source.
// ---------------------------------------------------------------------------

export const missingStepSchema = z.object({
  order: z.number(),
  intent: z.string(),
  typeKey: z.string(),
  activityId: z.string(),
  // Always null in every fixture observed. Kept as `unknown` rather than
  // `null` outright — if Cadence ever populates it, a receipt shouldn't fail
  // to parse over a field this schema was never told the shape of.
  evidenceFree: z.unknown().nullable(),
})
export type MissingStep = z.infer<typeof missingStepSchema>

/**
 * The shape the real fixture JSON files actually have today — no `steps`
 * field, because that field doesn't exist in real Cadence output yet
 * (addendum §G). `backend/src/cadence/fixtures.ts` validates the raw files
 * against this schema, enriches them with a synthesized `steps` array, then
 * validates the result against `receiptSchema` below.
 */
export const rawReceiptSchema = z.object({
  runId: z.string(),
  // Observed: "COMPLETED", "PARKED". Loosely typed for the same reason as
  // `outcome`/`verdict` above.
  state: z.string(),
  because: z.string(),
  missing: z.array(missingStepSchema),
  caveats: z.array(z.string()),
  checks: z.array(checkSchema),
  rows: z.array(eventRowSchema),
  exceptions: exceptionsSchema,
  triptych: triptychSchema,
  resolved: resolvedSchema,
  vocabularyVersion: z.string(),
  addressedTo: addressedToSchema,
})
export type RawReceipt = z.infer<typeof rawReceiptSchema>

// ---------------------------------------------------------------------------
// Money — addendum §H. The redaction rule governs what leaves the customer's
// control (published receipts, cross-tenant views), not what an authenticated
// operator sees of their own tenant's business, so amounts render throughout
// the console's own views. Nullable wherever a run genuinely has no computed
// total yet — a run parked before quote assembly has no total to show, and
// showing a fabricated one would be worse than showing none.
// ---------------------------------------------------------------------------

export const moneyAmountSchema = z
  .object({
    value: z.number(),
    currency: z.string(),
  })
  .nullable()
export type MoneyAmount = z.infer<typeof moneyAmountSchema>

export const receiptSchema = rawReceiptSchema.extend({
  /**
   * Addendum §G. Every declared step, always — not just the ones `missing`
   * happens to list. The fake server synthesizes this for the three fixture
   * runs; real Cadence integration (Phase 4) needs to emit it natively.
   */
  steps: z.array(declaredStepSchema),
  /**
   * Addendum §H. The run's own evidence-tier amount — Run Detail's view of
   * the business, not a field inside the signed receipt artifact itself
   * (this schema doesn't yet model a separate signed-envelope
   * representation; today's fixtures and this receipt shape are one and the
   * same object). Sourced identically to `RunSummary.amount` for the same
   * run — see `backend/src/cadence/fake-server/synthetic-runs.ts`.
   */
  amount: moneyAmountSchema,
  /**
   * Phase 6. The version fingerprint a mutation must present to act on this
   * run (phase plan: "approval binds to this exact version") — the same
   * value `RunSummary.fingerprint` carries for this run, so Run Detail
   * doesn't need a second fetch just to learn what to submit.
   */
  fingerprint: z.string(),
  /**
   * WHAT CADENCE IS ACTUALLY OFFERING ON THIS RUN — added 2026-08-19, CON-001.
   *
   * The contract puts a fingerprint on each *offered* action rather than one
   * on the run, and an empty list means there is nothing to do. That carries
   * strictly more information than the console's previous signal, which was
   * `exceptions.needsADecision.length > 0` — a field the contract does not
   * publish at all, so after the migration it was always empty and **every
   * run rendered with no actions whatsoever**.
   *
   * Driving the buttons from this instead means `/rule` lights up on its own
   * the day Cadence starts offering it (CAD-005), with no console change —
   * which is what the lead's ruling asked for.
   */
  availableActions: z.array(
    z.object({
      action: z.enum(['approve', 'refuse', 'rule']),
      fingerprint: z.string(),
      description: z.string(),
    }),
  ),
  /**
   * `LGH-027`. Whether approving THIS run commits to a write against the
   * customer's own books, straight from `ContractRunDetail.books_effect` —
   * the console's own argument for asking Cadence for the field was that it
   * belongs on the approve control at the moment of commitment, not as a
   * standing badge, so it travels with the run rather than living only in
   * the contract layer.
   */
  booksEffect: contractBooksEffectSchema,
  /** `PLT-054`, `LGH-029` §2. Same field, same derivation as `RunSummary.turnaround`. */
  turnaround: contractTurnaroundReadingSchema.nullable(),
})
export type Receipt = z.infer<typeof receiptSchema>
export type AvailableAction = Receipt['availableActions'][number]

// ---------------------------------------------------------------------------
// The Runs list / filter schema — one definition, three consumers (phase
// plan §0): Cadence's query parameters, the Runs page's filter controls, and
// the Ask surface's structured output. Ask cannot express a filter Runs
// can't render, and vice versa, because there is only one schema.
// ---------------------------------------------------------------------------

/**
 * UI spec §3's Verdict column conflates verdict (`confirmed`/`refused`) with
 * workflow state (`needs you`) — flagged as unresolved in addendum §F item 4.
 * This enum matches the spec as it stands today, not a resolution of that
 * question; revisit this the moment §F item 4 is ruled.
 *
 * ── `not_confirmed_yet` ADDED 2026-08-19, DURING CON-001 ──────────────────
 * The contract has five verdicts and this console had four. Four of the five
 * map cleanly (`doesnt_match` is our `mismatch`); the fifth had nowhere to go.
 *
 * It could not be folded into any of the others without the screen making a
 * claim the platform did not:
 *   - `confirmed` asserts an independent read agreed. Nothing agreed.
 *   - `mismatch` asserts a check came back against it. Nothing came back.
 *   - `needs_you` asserts the run is waiting on a decision. It is not.
 * The contract's own words are "Nothing has disagreed; nothing has confirmed
 * it either" — a distinct epistemic state, and per the contract's §2 note,
 * keeping "we could not tell" apart from "we checked and it disagrees" is
 * most of what this platform is for.
 *
 * Measured against real Cadence before widening: **63 of 81 runs (77.8%)**
 * are `not_confirmed_yet`. Collapsing it would have mislabelled four runs in
 * five, on the majority of every screen.
 *
 * This diverges from the lead's 2026-08-19 ruling, which described the
 * console vocabulary as staying four-valued. That ruling settled the
 * *architecture* (translate at one boundary) and did not name a target for
 * this value; flagged for confirmation rather than assumed either way.
 */
export const runVerdictSchema = z.enum([
  'confirmed',
  'refused',
  'needs_you',
  'mismatch',
  'not_confirmed_yet',
])
export type RunVerdict = z.infer<typeof runVerdictSchema>

export const runFilterSchema = z.object({
  verdict: runVerdictSchema.optional(),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  customer: z.string().optional(),
  actionType: z.string().optional(),
  ruleFired: z.string().optional(),
  /** Substring match over line-item descriptions — the Ask example "cable products". */
  lineItemContains: z.string().optional(),
  /**
   * The Ask example "runs above 10,000 AED" filters on an amount the Runs
   * list never displays (the redaction rule, addendum §F item 3, is about
   * what's *shown* in a list, not what a query can select on) — so this
   * stays in the filter schema even though no list column surfaces it.
   */
  amountAbove: z.number().optional(),
})
export type RunFilter = z.infer<typeof runFilterSchema>

export const runSortFieldSchema = z.enum(['time', 'customer', 'verdict', 'actionType', 'amount'])

export const runListQuerySchema = z.object({
  filter: runFilterSchema.default({}),
  sort: runSortFieldSchema.default('time'),
  sortDirection: z.enum(['asc', 'desc']).default('desc'),
  offset: z.number().int().min(0).default(0),
  limit: z.number().int().min(1).max(100).default(50),
})
export type RunListQuery = z.infer<typeof runListQuerySchema>

export const runSummarySchema = z.object({
  runId: z.string(),
  reference: z.string(),
  verdict: runVerdictSchema,
  customer: z.string().nullable(),
  actionType: z.string(),
  currency: z.string().nullable(),
  amount: moneyAmountSchema,
  fingerprint: z.string(),
  occurredAt: z.string(),
  /**
   * `PLT-054`, `LGH-029` §2. Null while the run has not completed — never a
   * zero, never an average. See `contractTurnaroundReadingSchema`; reused
   * directly rather than mirrored, same call `booksEffect` already made.
   */
  turnaround: contractTurnaroundReadingSchema.nullable(),
})
export type RunSummary = z.infer<typeof runSummarySchema>

export const runListResponseSchema = z.object({
  rows: z.array(runSummarySchema),
  totalCount: z.number(),
  /**
   * Rows the platform sent that this console could not read — `LGH-011` §1.
   *
   * Almost always 0. When it is not, **it is rendered**: a list that quietly
   * drops what it cannot parse looks complete, which is worse than one that
   * says so. Same reasoning as `excludedFixtures` on the tenant list.
   */
  unreadableRows: z.number().int().nonnegative(),
  asOf: z.string(),
})
export type RunListResponse = z.infer<typeof runListResponseSchema>

// ---------------------------------------------------------------------------
// Home aggregates — the four metrics (UI spec §2), computed by Cadence, not
// by the console paging the run list (phase plan §0). Deliberately data-only:
// numerator/denominator pairs rather than a pre-formatted percentage, so
// "not confirmed yet" can't hide inside a bare percent. `attention` carries no
// trend — a point-in-time queue depth doesn't have a week-over-week trend
// worth showing (phase plan, Phase 5) — but the other three do, matching the
// UI spec §2 mockup's ↑/↓/→ indicators. `totalValue` (addendum §H) is `null`
// when the contributing runs span more than one currency — summing mixed
// currencies isn't a number, it's a category error — or when there's nothing
// to sum.
// ---------------------------------------------------------------------------

export const trendDirectionSchema = z.enum(['up', 'down', 'same'])
export type TrendDirection = z.infer<typeof trendDirectionSchema>

const trendFieldsSchema = z.object({
  trend: trendDirectionSchema,
  delta: z.number(),
})

export const homeAggregatesSchema = z.object({
  runsLast7Days: z
    .object({ count: z.number(), totalValue: moneyAmountSchema })
    .extend(trendFieldsSchema.shape),
  autonomy: z
    .object({ autonomousCount: z.number(), totalCount: z.number() })
    .extend(trendFieldsSchema.shape),
  attention: z.object({
    openCount: z.number(),
    oldestItemAgeMs: z.number().nullable(),
    totalValue: moneyAmountSchema,
    /**
     * `LGH-031`. A sub-count of `openCount` — how many of the same open runs
     * are additionally held by a credit, margin, or discount gate. Always
     * `<= openCount`. A genuine zero is good news, not "not measured" — no
     * separate null state exists because the underlying count is always
     * computable from the same rows `openCount` already reads.
     */
    policyHeldCount: z.number(),
  }),
  verified: z
    .object({ verifiedCount: z.number(), totalCount: z.number() })
    .extend(trendFieldsSchema.shape),
  asOf: z.string(),
})
export type HomeAggregates = z.infer<typeof homeAggregatesSchema>

// ---------------------------------------------------------------------------
// The attention queue — Home's "What needs you" section (UI spec §2, Section
// 2). A dedicated shape, not a filtered slice of `RunSummary`: `title`,
// `detail`, `waitingSince`, and `action` are queue-specific framings that
// don't belong on the general-purpose run list, and `urgency` is Cadence's
// classification (phase plan Phase 5: "risk tier from the action taxonomy
// first, age within tier second" — a stated function, not a comparator the
// console invents; see `classifyAttentionUrgency` in `attention.ts`).
// ---------------------------------------------------------------------------

/**
 * `refused` added 2026-08-19 during CON-001. The contract's four reasons are
 * `approval_needed | ambiguity | mismatch | refused`; this console's four were
 * the same minus `refused`, plus `rule_needed`.
 *
 * `refused` had nowhere to go and could not be folded: the other three all
 * describe something unresolved, and a refused run is resolved — it stopped
 * on purpose. Labelling it as any of them would put a live-risk framing on a
 * closed decision.
 *
 * `rule_needed` stays and is now unreachable from real Cadence, which is
 * consistent rather than dead: the lead ruled that the contract will not gain
 * it and that `ambiguity` is what arrives instead, and `/rule` is 501 by
 * design for v1. It remains the console's own concept for when that lands.
 */
export const attentionReasonSchema = z.enum([
  'approval_needed',
  'rule_needed',
  'mismatch',
  'ambiguity',
  'refused',
])
export type AttentionReason = z.infer<typeof attentionReasonSchema>

export const attentionUrgencySchema = z.enum(['high', 'medium', 'low'])
export type AttentionUrgency = z.infer<typeof attentionUrgencySchema>

export const attentionActionSchema = z.enum(['review', 'rule'])
export type AttentionAction = z.infer<typeof attentionActionSchema>

/**
 * THE CASE'S OWN LIFECYCLE, and the console renames none of it.
 *
 * Unlike verdicts — where the console's four meet the contract's five in
 * `mapping.ts` — these six are carried through unchanged. There is nothing to
 * translate: they are the platform's lifecycle, and a console-side synonym
 * would be a second name for one fact.
 *
 * **`contract-read.ts` holds the same six and `cadence.test.ts` asserts the
 * two agree**, so a state added upstream fails here rather than being silently
 * dropped on the floor by a screen that only knows five.
 */
export const attentionCaseStatusSchema = z.enum([
  'in_flight',
  'needs_review',
  'succeeded',
  'succeeded_with_warning',
  'failed',
  'manually_resolved',
])
export type AttentionCaseStatus = z.infer<typeof attentionCaseStatusSchema>

export const attentionItemSchema = z.object({
  runId: z.string(),
  reference: z.string().nullable(),
  urgency: attentionUrgencySchema,
  reason: attentionReasonSchema,
  /**
   * **What the CASE is, as opposed to why this row is listed** — `PLT-027`,
   * and the contract is explicit that the two are not redundant:
   *
   *   *"`case_status` is authoritative for the lifecycle; `reason` is
   *   authoritative for the ordering. Neither is derivable from the other…
   *   the screen should not pick one."*
   *
   * So the queue renders both, and nothing here computes one from the other.
   */
  caseStatus: attentionCaseStatusSchema,
  title: z.string(),
  detail: z.string(),
  amount: moneyAmountSchema,
  waitingSince: z.string(),
  action: attentionActionSchema,
  /**
   * `PLT-063`, `LGH-029` §1. Additive to `reason` — a case can carry a
   * `reason` and one or more of these, and neither hides the other. `[]`
   * when nothing extra flags this item. See `contractPolicyFlagSchema`.
   */
  policyFlags: z.array(contractPolicyFlagSchema),
})
export type AttentionItem = z.infer<typeof attentionItemSchema>
export type PolicyFlagCode = z.infer<typeof contractPolicyFlagCodeSchema>

export const attentionQueueResponseSchema = z.object({
  items: z.array(attentionItemSchema),
  totalCount: z.number(),
  /** Queue items this console could not read. Rendered, never silently dropped. */
  unreadableItems: z.number().int().nonnegative(),
  asOf: z.string(),
})
export type AttentionQueueResponse = z.infer<typeof attentionQueueResponseSchema>

// ---------------------------------------------------------------------------
// External system health — feeds the degradation banner (addendum §A).
// ---------------------------------------------------------------------------

export const externalSystemStatusSchema = z.enum(['ok', 'degraded', 'unreachable'])

/**
 * What Cadence itself reports about the one external system *it* integrates
 * with. The console backend's own reachability of Cadence is inferred from
 * whether this call succeeds at all, not carried as a field inside it — and
 * LLM-provider health is the console's own knowledge (Phase 7), not
 * Cadence's. `externalHealthSchema` below is the console assembling all
 * three into the one shape the frontend's degradation banner consumes.
 */
export const cadenceHealthSchema = z.object({
  zoho: z.object({
    status: externalSystemStatusSchema,
    detail: z.string().nullable(),
  }),
  asOf: z.string(),
})
export type CadenceHealth = z.infer<typeof cadenceHealthSchema>

/**
 * How the console came to believe which tenant Cadence's data belongs to.
 *
 * - `reported` — Cadence named the tenant in its own response. The console
 *   verified it. Not achievable today: no v1 route returns a tenant
 *   identifier (all 19 enumerated, `/system/health` included), which is why
 *   `REQ-console-tenant-identity` is filed.
 * - `configured` — the console was *told*, via `CADENCE_TENANT_ID`. A claim
 *   by whoever deployed it, not a fact the console checked.
 * - `unknown` — nothing configured and nothing reported. The console does not
 *   know whose data it is rendering, and says so.
 *
 * The distinction is the whole point of the type. Collapsing `reported` and
 * `configured` into a bare tenant id would let the console present a guess
 * with the same confidence as a verified fact, which is the failure this
 * exists to prevent.
 */
export const provenanceSourceSchema = z.enum(['reported', 'configured', 'unknown'])
export type ProvenanceSource = z.infer<typeof provenanceSourceSchema>

/**
 * Whose data is on the screen, and how sure the console is.
 *
 * Deliberately *not* folded into the identity the console renders in its
 * header. That identity answers "who are you" from the signed-in session;
 * this answers "whose runs are these" from the upstream connection. They are
 * resolved by two unrelated systems and can disagree — treating them as one
 * value is exactly how a console ends up labelling another tenant's data with
 * the signed-in user's name.
 */
export const dataProvenanceSchema = z.object({
  /** Host of `CADENCE_BASE_URL`. Host only — the path carries nothing an operator needs. */
  host: z.string(),
  /** Null when `source` is `unknown`. */
  tenantId: z.string().nullable(),
  /** A human-readable name, when one is known. Null is normal, not an error. */
  tenantLabel: z.string().nullable(),
  source: provenanceSourceSchema,
  /**
   * Whether this matches the tenant of the signed-in session. `null` when
   * `source` is `unknown` — "we cannot tell" is a third answer, and rendering
   * it as `false` would claim a mismatch the console has not established.
   */
  matchesSession: z.boolean().nullable(),
})
export type DataProvenance = z.infer<typeof dataProvenanceSchema>

export const externalHealthSchema = z.object({
  zoho: z.object({
    status: externalSystemStatusSchema,
    detail: z.string().nullable(),
  }),
  llmProvider: z.object({
    status: externalSystemStatusSchema,
    detail: z.string().nullable(),
  }),
  cadence: z.object({
    status: externalSystemStatusSchema,
    detail: z.string().nullable(),
  }),
  /**
   * Carried here rather than on its own endpoint because the shell already
   * polls this one, and because when `REQ-console-tenant-identity` lands the
   * tenant will arrive on Cadence's `/system/health` — the same call this
   * field is assembled from. The field's shape does not change when its
   * source does; only `source` flips from `configured` to `reported`.
   */
  provenance: dataProvenanceSchema,
  asOf: z.string(),
})
export type ExternalHealth = z.infer<typeof externalHealthSchema>

/**
 * The same shape minus `provenance` — everything `CadenceClient` can know on
 * its own. Provenance needs the signed-in tenant to compare against, and the
 * client has no session context, so the route assembles the last field. Same
 * reason `llmProvider` is overridden there: the client cannot see the tenant's
 * daily spend either.
 *
 * Derived by `.omit()` rather than written out, so the two cannot drift.
 */
export const upstreamHealthSchema = externalHealthSchema.omit({ provenance: true })
export type UpstreamHealth = z.infer<typeof upstreamHealthSchema>

// ---------------------------------------------------------------------------
// Phase 6 mutations — approve, refuse, rule. Each is conditional on the
// run's current `fingerprint` (phase plan, Phase 6: "approval binds to this
// exact version"), the same discipline for all three per addendum §I ("the
// atomic ruling action... to the same standard as Approve/Refuse"). A
// mismatched fingerprint means the run changed since the confirmation
// screen was fetched — the server returns a conflict, never silently acts
// on stale intent.
// ---------------------------------------------------------------------------

export const approveRequestSchema = z.object({
  fingerprint: z.string(),
})
export type ApproveRequest = z.infer<typeof approveRequestSchema>

export const refuseRequestSchema = z.object({
  fingerprint: z.string(),
  reason: z.string().min(1),
})
export type RefuseRequest = z.infer<typeof refuseRequestSchema>

/**
 * Addendum §I: ruling is atomic and single-run — this mutation resolves the
 * run in front of the operator, never a rules-authoring action. No
 * persistence mechanism (customer-identity memory, catalogue-alias memory,
 * rule-encoding) is part of this request; those are deliberately deferred,
 * separate follow-up flows, not fields on this schema.
 */
export const ruleRequestSchema = z.object({
  fingerprint: z.string(),
  ruling: z.string().min(1),
})
export type RuleRequest = z.infer<typeof ruleRequestSchema>

/**
 * The one response shape all three mutations succeed with — the run's full,
 * freshly-mutated receipt, so the caller can render the result directly
 * without a second round trip.
 */
export const mutationResultSchema = z.object({
  receipt: receiptSchema,
})
export type MutationResult = z.infer<typeof mutationResultSchema>

/**
 * What a fingerprint mismatch returns — HTTP 409, this body. Carries the
 * run's actual current state so the console's conflict screen can show what
 * changed, not just that something did.
 */
export const mutationConflictSchema = z.object({
  error: z.literal('conflict'),
  message: z.string(),
  currentReceipt: receiptSchema,
})
export type MutationConflict = z.infer<typeof mutationConflictSchema>

/** What a mutation endpoint actually returns — 200 or 409, one response type either way, so the frontend's fetch wrapper can `alsoAccept` the 409 and discriminate on `'error' in result` rather than catching a thrown error to get at the conflict body. */
export const mutationResponseSchema = z.union([mutationResultSchema, mutationConflictSchema])
export type MutationResponse = z.infer<typeof mutationResponseSchema>
